import { c as _c } from "react/compiler-runtime";
import { NO_TRANSLATE_CLASS, Text } from "@flat-ui";
import { ANALYTICS_REQUIRED_TABLE_COLUMNS, METRIC_CHART_METADATA } from "@components/analytics/in-house/constants/analytics-constants";
import { useMemo } from "react";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { DateCell } from "@components/common/date-cell";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
const requiredAnalyticsTableColumnsSet = new Set(ANALYTICS_REQUIRED_TABLE_COLUMNS);

/** Type guard to check if a string is a valid ProjectsTableColumnField. */
function isProjectsTableColumnField(key) {
  return requiredAnalyticsTableColumnsSet.has(key);
}

/**
 * FLEX_WEIGHTS defines the relative flex-grow weights for resizable columns in the projects table.
 * Higher values make columns expand more when there is extra space.
 * Only columns listed here will have a custom flex value; others use the default.
 */
const FLEX_WEIGHTS = {
  projectName: 1
};

/** Returns configured columns for the analytics projects breakdown table. */
export function useAnalyticsProjectsBreakdownColumns(selectedMetricKey) {
  const $ = _c(21);
  const {
    columnPreferences,
    visibleOrderedColumns
  } = useColumnPreferencesContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      field: "projectName",
      headerName: "Name",
      flex: FLEX_WEIGHTS.projectName,
      minWidth: 240,
      sortable: true,
      disableReorder: true,
      renderCell: _temp,
      valueGetter: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = METRIC_CHART_METADATA[selectedMetricKey];
  let t2;
  let t3;
  if ($[1] !== selectedMetricKey) {
    t2 = params_1 => {
      const metricValue = params_1.row.entity.metrics[selectedMetricKey];
      return metricValue ?? "-";
    };
    t3 = (__0, row_0) => row_0.entity.metrics[selectedMetricKey] ?? 0;
    $[1] = selectedMetricKey;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1.tableColumnLabel || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      field: "totalViews",
      headerName: t1.tableColumnLabel,
      minWidth: 160,
      sortable: true,
      renderHeader: _temp3,
      renderCell: t2,
      valueGetter: t3
    };
    $[4] = t1.tableColumnLabel;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      field: "projectManager",
      headerName: "Project manager",
      minWidth: 175,
      sortable: true,
      renderHeader: _temp4,
      renderCell: _temp5
    };
    t6 = {
      field: "group",
      headerName: "Group",
      minWidth: 175,
      sortable: true,
      renderHeader: _temp6,
      renderCell: _temp7
    };
    t7 = {
      field: "createdDate",
      headerName: "Created",
      minWidth: 180,
      sortable: true,
      renderHeader: _temp8,
      renderCell: _temp9,
      valueGetter: _temp0
    };
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== columnPreferences) {
    t8 = {
      field: "options",
      align: "right",
      maxWidth: 90,
      minWidth: 90,
      disableReorder: true,
      renderHeader: () => <FaroTableColumnVisibilityMenuIcon columnPreferences={columnPreferences} />,
      renderCell: _temp1,
      sortable: false
    };
    $[11] = columnPreferences;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t4 || $[14] !== t8) {
    t9 = {
      projectName: t0,
      totalViews: t4,
      projectManager: t5,
      group: t6,
      createdDate: t7,
      options: t8
    };
    $[13] = t4;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const columnsMap = t9;
  let t10;
  if ($[16] !== columnsMap || $[17] !== visibleOrderedColumns) {
    let t11;
    if ($[19] !== columnsMap) {
      t11 = header => columnsMap[header];
      $[19] = columnsMap;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    t10 = visibleOrderedColumns.filter(isProjectsTableColumnField).map(t11);
    $[16] = columnsMap;
    $[17] = visibleOrderedColumns;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const orderedColumns = t10;
  return orderedColumns;
}
function _temp1() {
  return null;
}
function _temp0(__1, row_1) {
  return new Date(row_1.entity.createdDate).getTime();
}
function _temp9(params_5) {
  return <DateCell date={params_5.row.entity.createdDate} />;
}
function _temp8(params_4) {
  return <TableHeaderWithDraggableIcon title={params_4.colDef.headerName ?? ""} />;
}
function _temp7() {
  return "-";
}
function _temp6(params_3) {
  return <TableHeaderWithDraggableIcon title={params_3.colDef.headerName ?? ""} />;
}
function _temp5() {
  return "-";
}
function _temp4(params_2) {
  return <TableHeaderWithDraggableIcon title={params_2.colDef.headerName ?? ""} />;
}
function _temp3(params_0) {
  return <TableHeaderWithDraggableIcon title={params_0.colDef.headerName ?? ""} />;
}
function _temp2(_, row) {
  return row.entity.projectName;
}
function _temp(params) {
  return <Text variant="bodyS" className={NO_TRANSLATE_CLASS}>{params.row.entity.projectName}</Text>;
}